'use client';

import { useState } from 'react';
import Image from 'next/image';
import { useToast } from '@/src/components/ui/ToastNotifications';
import { useProfile } from '@/src/hooks/use-profile';
import { Icon } from '@/src/components/ui/Icon';
import { Spinner } from '@/src/components/ui';
import { Button } from '@workspace/ui/components/button';
import { addProjectReview } from '@/src/actions/reviews';
import { IReview, StarValue } from 'types/src/types';

const RATING_LABELS: Record<StarValue, string> = {
  1: 'Bad',
  2: 'Poor',
  3: 'Mixed',
  4: 'Good',
  5: 'Excellent',
};

type Props = {
  projectId: number;
  onReviewAdded?: (review: IReview) => void;
};

export const RatingInput = ({ projectId, onReviewAdded }: Props) => {
  const [rating, setRating] = useState<StarValue | null>(null);
  const [hoverRating, setHoverRating] = useState<StarValue | null>(null);
  const [review, setReview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const { data: profile } = useProfile();

  const handleSubmit = async () => {
    if (!rating || !review.trim()) return;
    setIsSubmitting(true);

    try {
      const { data, error } = await addProjectReview(projectId, {
        rating,
        text: review.trim(),
      });

      if (error) {
        toast.error('Error submitting review. Please try again.');
        return;
      }

      const author = profile
        ? {
            id: profile.id!,
            name: profile.fullName,
            avatarUrl: profile.avatarUrl ?? null,
            uniqueLink: profile.uniqueLink ?? '',
          }
        : undefined;

      const newReview: IReview =
        data && 'id' in data
          ? {
              id: data.id,
              projectId: data.projectId ?? projectId,
              authorId: data.authorId ?? author?.id ?? 0,
              rating: data.rating,
              text: data.text,
              createdAt: new Date(data.createdAt ?? Date.now()),
              author,
            }
          : {
              id: Math.random(),
              projectId,
              authorId: author?.id ?? 0,
              rating,
              text: review.trim(),
              createdAt: new Date(),
              author,
            };

      onReviewAdded?.(newReview);

      setRating(null);
      setReview('');
      toast.success('Your review was published');
    } catch (e) {
      console.error('Error submitting review:', e);
      toast.error('Error submitting review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      handleSubmit();
      return;
    }
  };

  const displayRating = hoverRating || rating;

  return (
    <div className="">
      <div className="flex items-center gap-4 mb-6">
        <Image
          src={profile?.avatarUrl || '/images/user.png'}
          alt="User avatar"
          className="size-8 rounded-full"
          width={32}
          height={32}
        />
        <h2 className="text-lg! font-bold text-gray-800">
          Rate and add review
        </h2>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onMouseEnter={() => setHoverRating(star as StarValue)}
                onMouseLeave={() => setHoverRating(null)}
                onClick={() => setRating(star as StarValue)}
                className="transition-transform hover:scale-110 focus:outline-none"
                aria-label={`Rate ${star} stars`}
              >
                <Icon
                  name="star"
                  className={`size-8 transition-colors 
                  ${
                    displayRating && star <= displayRating
                      ? 'text-black'
                      : 'text-transparent stroke-black/20 stroke-[0.5px]'
                  }`}
                />
              </button>
            ))}
          </div>

          <div className="mb-5 mt-2.5">
            <span className="text-sm font-semibold text-gray-700">
              {displayRating
                ? `(${RATING_LABELS[displayRating]})`
                : 'Select rating'}
            </span>
          </div>
        </div>
        <Button
          onClick={handleSubmit}
          size="sm"
          variant="secondary"
          disabled={isSubmitting || !rating}
          className="p-3"
        >
          {isSubmitting ? <Spinner /> : 'Publish'}
        </Button>
      </div>

      <div className="mb-6">
        <textarea
          value={review}
          onChange={(e) => setReview(e.target.value)}
          placeholder="Write your review..."
          className="w-full min-h-[120px] p-4 text-lg border-2 border-gray-200 rounded-lg focus:border-gray-400 focus:outline-none resize-none bg-bg-gray"
          disabled={isSubmitting}
          onKeyDown={handleKeyDown}
        />
      </div>
    </div>
  );
};
